// src/adapters/propertyVideoAdapter.ts
/**
 * Property Video Adapter
 * Implements IFullVideoGeneratorAdapter for property video generation
 */

import { useMemo } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import type {
  IFullVideoGeneratorAdapter,
  IVideoForConcatenation,
  ICreateFinalVideoParams,
} from '@/@types/fullVideoGenerator';

interface IPropertySceneForConcat {
  id: string;
  sourceId: string;
  order: number;
  videoUrl: string | null;
  muxedVideoUrl: string | null;
  muxedStatus?: 'pending' | 'processing' | 'completed' | 'failed';
  voiceoverDurationSeconds: number;
}

/**
 * Hook to create a property video adapter
 * Uses Convex hooks internally for reactivity
 * Supports null propertyVideoId for conditional usage (skip pattern)
 */
export function usePropertyVideoAdapter(propertyVideoId: Id<'propertyVideos'> | null): {
  adapter: IFullVideoGeneratorAdapter | null;
  scenesForConcat: IPropertySceneForConcat[] | undefined;
  isReady: boolean;
  readyMessage: string;
  totalScenes: number;
  readyScenes: number;
  needsMuxing: number;
} {
  // Use "skip" pattern when propertyVideoId is null
  const scenesForConcat = useQuery(
    api.property.queries.getPropertyScenesForConcatenation,
    propertyVideoId ? { propertyVideoId } : 'skip'
  );
  const allScenes = useQuery(
    api.property.queries.getPropertyScenes,
    propertyVideoId ? { propertyVideoId } : 'skip'
  );
  const generateUploadUrlMutation = useMutation(
    api.property.mutations.generatePropertyVideoUploadUrl
  );
  const createFinalVideoMutation = useMutation(api.property.mutations.createPropertyFinalVideo);

  // Calculate readiness - now requires muxed videos (video + audio combined)
  const { isReady, readyMessage, totalScenes, readyScenes, needsMuxing } = useMemo(() => {
    if (!scenesForConcat || !allScenes) {
      return {
        isReady: false,
        readyMessage: 'Loading...',
        totalScenes: 0,
        readyScenes: 0,
        needsMuxing: 0,
      };
    }

    const total = allScenes.length;
    // Ready = scenes with completed muxed video
    const ready = scenesForConcat.filter(
      (s: IPropertySceneForConcat) => s.muxedVideoUrl !== null && s.muxedStatus === 'completed'
    ).length;
    // Scenes with video but need muxing
    const muxingNeeded = scenesForConcat.filter(
      (s: IPropertySceneForConcat) =>
        s.videoUrl !== null && (s.muxedVideoUrl === null || s.muxedStatus !== 'completed')
    ).length;

    if (total === 0) {
      return {
        isReady: false,
        readyMessage: 'No scenes created',
        totalScenes: 0,
        readyScenes: 0,
        needsMuxing: 0,
      };
    }

    if (muxingNeeded > 0) {
      return {
        isReady: false,
        readyMessage: `${muxingNeeded} scene(s) need audio muxing`,
        totalScenes: total,
        readyScenes: ready,
        needsMuxing: muxingNeeded,
      };
    }

    if (ready !== total) {
      const missing = total - ready;
      return {
        isReady: false,
        readyMessage: `${missing} scene(s) need video generation`,
        totalScenes: total,
        readyScenes: ready,
        needsMuxing: 0,
      };
    }

    return {
      isReady: true,
      readyMessage: `${ready} scenes ready`,
      totalScenes: total,
      readyScenes: ready,
      needsMuxing: 0,
    };
  }, [scenesForConcat, allScenes]);

  // Return null adapter when propertyVideoId is null
  const adapter: IFullVideoGeneratorAdapter | null = useMemo(() => {
    if (!propertyVideoId) {
      return null;
    }

    return {
      videoType: 'property',
      targetResolution: { width: 1080, height: 1920 },

      getVideosForConcatenation: async (): Promise<IVideoForConcatenation[]> => {
        if (!scenesForConcat) {
          throw new Error('No videos available');
        }

        // Use muxed videos (video + audio combined) for final concatenation
        const validVideos = scenesForConcat.filter(
          (s: IPropertySceneForConcat) => s.muxedVideoUrl !== null && s.muxedStatus === 'completed'
        );

        if (validVideos.length === 0) {
          throw new Error('No scenes with muxed videos. Please mux all scenes first.');
        }

        return validVideos.map((s: IPropertySceneForConcat) => ({
          id: s.id,
          sourceId: s.sourceId,
          order: s.order,
          videoUrl: s.muxedVideoUrl!, // Use muxed video URL (has audio)
          voiceoverDurationSeconds: s.voiceoverDurationSeconds,
        }));
      },

      generateUploadUrl: async (): Promise<string> => {
        return await generateUploadUrlMutation();
      },

      createFinalVideoRecord: async (params: ICreateFinalVideoParams): Promise<string> => {
        const videoId = await createFinalVideoMutation({
          propertyVideoId,
          storageId: params.storageId,
          durationSeconds: params.durationSeconds,
          fileSizeBytes: params.fileSizeBytes,
          sourceSceneIds: params.sourceVideoIds as Id<'propertyScenes'>[],
        });
        return videoId;
      },
    };
  }, [propertyVideoId, scenesForConcat, generateUploadUrlMutation, createFinalVideoMutation]);

  return {
    adapter,
    scenesForConcat,
    isReady,
    readyMessage,
    totalScenes,
    readyScenes,
    needsMuxing,
  };
}
